// Edit energy initiative
// react
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

// material ui
import { Typography } from '@mui/material';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import EditInitiativeForm from '@/features/energyxplorer/forms/EditInitiativeForm';
// components
import ErrorBoundary from '@/routes/-ErrorBoundary.tsx';

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/deepdive/$energyGroupId/energy-initiatives/$initiativeId/edit',
)({
  component: EditInitiativePage,
  errorComponent: ErrorBoundary,
  staticData: { crumb: 'common:actions.edit', hideEnergyAnalysisAppBar: true },
});

function EditInitiativePage() {
  const { t } = useTranslation(['energyxplorer-initiatives-tab', 'forms']);
  const navigate = useNavigate();
  const { energyGroupId, initiativeId } = useParams({ strict: false });

  const { data: energyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );

  const initiative = useMemo(
    () =>
      (energyGroup?.energyInitiatives || []).find(
        (i) => String(i.energyInitiativeId) === String(initiativeId),
      ),
    [energyGroup?.energyInitiatives, initiativeId],
  );

  const handleCancel = () => {
    navigate({
      to: '../../',
      search: (prev: Record<string, string>) => ({ ...prev }),
    });
  };

  if (!initiative) {
    return (
      <PageContainer
        maxWidth="xl"
        sx={{ py: 2 }}
        data-testid="edit-initiative-page"
      >
        <PageHeader title={t('dialogs.edit.title')} to="../../" />
        <Typography sx={{ mt: 2 }}>
          {t('dialogs.edit.messages.no_nodes_found')}
        </Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      sx={{ py: 2 }}
      data-testid="edit-initiative-page"
    >
      <PageHeader title={t('dialogs.edit.title')} to="../../" />
      <EditInitiativeForm
        key={initiative.energyInitiativeId}
        energyGroup={energyGroup}
        initiative={initiative}
        onCancel={handleCancel}
      />
    </PageContainer>
  );
}
